/**
 * Helpers for the AI accessibility test rules (`EA_AI_R*`).
 *
 * AI rules arrive in the combined crawler report exactly like analytical rules,
 * distinguished only by their underscore id prefix. When the producer omits the
 * explicit `aiCategory` field, the eight test categories are derived from the
 * rule-number range. AI per-finding image assets live on a different CDN host
 * than the report.
 */

import type { WcagLevel } from '@eye-able/types';

/**
 * The effective WCAG conformance level for a rule row.
 *
 * Analytical rules carry a resolved `wcagRule.wcagLvl`. AI rules frequently have
 * no `wcagRule` object (their WCAG chapter isn't in `wcagRules`), but their meta
 * declares an explicit `conformanceLevel`. Falling back to it keeps AI rows
 * visible under the critical-only filter (which filters on `A`/`AA`) and present
 * — with a level — in bookmarks and report exports. Mirrors the fallback the
 * page-detail table already uses.
 *
 * EXCEPTION (checked first): an AI rule (`aiCategory` present) whose meta
 * explicitly declares `conformanceLevel: "Best Practice"` is a QUALITY finding
 * that *references* a WCAG chapter without failing it — e.g. `EA_AI_R44`
 * (broken skip link on a page whose landmarks still satisfy 2.4.1). The
 * chapter-derived level must NOT override that: otherwise filtering, top
 * issues and the accessibility statement count it as a Level-A failure.
 */
export function getEffectiveWcagLevel(
  ruleObj:
    | { wcagRule?: { wcagLvl?: WcagLevel | null } | null }
    | null
    | undefined,
  eaRule:
    | { conformanceLevel?: WcagLevel | null; aiCategory?: string | null }
    | null
    | undefined,
): WcagLevel | undefined {
  if (eaRule?.aiCategory && eaRule?.conformanceLevel === 'Best Practice') {
    return 'Best Practice' as WcagLevel;
  }
  return ruleObj?.wcagRule?.wcagLvl ?? eaRule?.conformanceLevel ?? undefined;
}

/** Base URL where the AI test publishes per-finding image assets (context + original). */
export const AI_ASSETS_CDN_BASE = 'https://eye-able-audit.b-cdn.net/';

/** AI test rule ids use the underscore form, e.g. "EA_AI_R12". */
const AI_RULE_PREFIX = 'EA_AI_R';
/** Defensive: some sources hand us the hyphen form, e.g. "EA-AI-R12". */
const AI_RULE_PREFIX_HYPHEN = 'EA-AI-R';

export type AiRuleCategory =
  | 'images'
  | 'lists'
  | 'tables'
  | 'headings'
  | 'links'
  | 'controls'
  | 'keyboard'
  | 'inputAssistance'
  | 'pages'
  | 'bypass'
  | 'zoom';

/**
 * True for AI-test rules (`EA_AI_R*`). Accepts the hyphen form defensively.
 * Non-string ids (e.g. the NUMERIC ruleId the ignore store / hidden-rules
 * settings carry for analytical rows) are never AI rules — guard the type so
 * `.startsWith` can't throw on a number.
 */
export function isAiRule(ruleId: string | number | null | undefined): boolean {
  if (typeof ruleId !== 'string') return false;
  return (
    ruleId.startsWith(AI_RULE_PREFIX) ||
    ruleId.startsWith(AI_RULE_PREFIX_HYPHEN)
  );
}

/**
 * Extracts the trailing rule number from any eye-able rule id, regardless of
 * prefix: `EA-R12`, `EA_AI_R12` and `EA-AI-R12` all yield `12`. Pass-through for
 * a numeric id. Returns null when there's no trailing `R<number>` (or the id is
 * neither string nor number). Use this instead of
 * `parseInt(id.replace('EA-R', ''))` so a new rule prefix doesn't need a new
 * parse site (see PR #945 review).
 */
export function extractRuleNumber(
  ruleId: string | number | null | undefined,
): number | null {
  if (typeof ruleId === 'number') {
    return Number.isFinite(ruleId) ? ruleId : null;
  }
  if (typeof ruleId !== 'string') return null;
  const match = /R(\d+)$/.exec(ruleId);
  return match ? Number(match[1]) : null;
}

/** Extracts the numeric part of an AI rule id ("EA_AI_R12" -> 12), or null. */
export function getAiRuleNumber(
  ruleId: string | null | undefined,
): number | null {
  return isAiRule(ruleId) ? extractRuleNumber(ruleId) : null;
}

/**
 * Maps an AI rule to one of the test categories. Prefers the `aiCategory`
 * field the producer sets in `ai_rules_meta.json`; falls back to deriving it from
 * the rule-number range when the meta field is absent (older meta / a rule the
 * catalogue doesn't carry yet):
 *   R1–11 + R33–36 = images, R12–16 = lists, R17–23 + R29 = tables,
 *   R24–28 = headings, R30–32 = links, R37–39 + R74 = pages, R40–42 = zoom,
 *   R43–47 = bypass, R48–56 = controls,
 *   R58–65 + R66 + R71 = keyboard, R67–70 + R73 = inputAssistance.
 * (R34–36 are the icon rules; icons live under the images category.)
 */
export function getAiRuleCategory(
  ruleId: string | null | undefined,
  eaRule?: { aiCategory?: AiRuleCategory | null } | null,
): AiRuleCategory | null {
  if (eaRule?.aiCategory) return eaRule.aiCategory;
  const n = getAiRuleNumber(ruleId);
  if (n === null) return null;
  if ((n >= 33 && n <= 36) || (n >= 1 && n <= 11)) return 'images';
  if (n >= 12 && n <= 16) return 'lists';
  if (n === 29 || (n >= 17 && n <= 23)) return 'tables';
  if (n >= 24 && n <= 28) return 'headings';
  if (n >= 30 && n <= 32) return 'links';
  if ((n >= 37 && n <= 39) || n === 74) return 'pages';
  if (n >= 40 && n <= 42) return 'zoom';
  if (n >= 43 && n <= 47) return 'bypass';
  if (n >= 48 && n <= 56) return 'controls';
  if (n >= 58 && n <= 65) return 'keyboard';
  // 2.II: R66 + R71 are keyboard-activation rules; R67–R70 + R73 are input-assistance.
  if (n === 66 || n === 71) return 'keyboard';
  if ((n >= 67 && n <= 70) || n === 73) return 'inputAssistance';
  return null;
}

/** True if the AI rule is an image rule (gets an original-image preview + alt-text suggestion). */
export function isAiImageRule(
  ruleId: string | null | undefined,
  eaRule?: { aiCategory?: AiRuleCategory | null } | null,
): boolean {
  return getAiRuleCategory(ruleId, eaRule) === 'images';
}

/**
 * True if the AI rule is an ICON rule (R34/R35/R36). Icons live under the
 * images category but render differently: NO downloadable original image (the
 * glyph has no asset) and the suggested fix / detected value is an ACCESSIBLE
 * NAME, not an alt attribute.
 */
export function isAiIconRule(ruleId: string | null | undefined): boolean {
  const n = getAiRuleNumber(ruleId);
  return n !== null && n >= 34 && n <= 36;
}

/**
 * True for rule rows that must be EXCLUDED from a legal accessibility
 * statement's non-conformity list: AI Best-Practice rules (e.g. EA_AI_R38
 * "page title could be improved") explicitly do NOT represent a WCAG
 * non-conformity — by definition the success criterion is met. Analytical
 * Best-Practice rules keep their long-standing behaviour under the
 * "AA + Best Practice" target.
 *
 * SINGLE predicate shared by statement GENERATION (Steps.tsx) and the saved-
 * statement COMPARISON (CompareStatementComponent.tsx) — diverging filters
 * would flag every statement as permanently outdated whenever such a rule
 * fires.
 */
export function isStatementExcludedAiRule(error: {
  eyeAbleId?: string | null;
  wcagRule?: { wcagLvl?: string | null } | null;
  eaRule?: { conformanceLevel?: string | null } | null;
}): boolean {
  return (
    isAiRule(error.eyeAbleId) &&
    (error.wcagRule?.wcagLvl ?? error.eaRule?.conformanceLevel) ===
      'Best Practice'
  );
}

/**
 * True when an AI rule that has NO rules_text.json entry can still produce a
 * statement sentence via the generic fallback (`getAiRuleFallbackSentences`
 * builds one from the rule's localized `content` — the safety net for newly
 * added AI rules). Statement GENERATION (Steps.tsx) includes such rules in
 * `reportIssues`; the saved-statement COMPARISON (CompareStatementComponent)
 * must apply the SAME inclusion, or a saved statement reads as permanently
 * outdated whenever such a rule fires.
 */
export function hasAiFallbackStatementSentence(error: {
  eyeAbleId?: string | null;
  eaRule?: { content?: string | null } | null;
}): boolean {
  return isAiRule(error.eyeAbleId) && !!error.eaRule?.content;
}

/** Directory the AI test publishes per-finding assets under, on the CDN and locally. */
const AI_ASSETS_DIR = 'ally-ai-testing-assets/';

/**
 * Resolves an AI asset path (context/original screenshot) to a full URL.
 * - Absolute http(s) URLs are returned as-is.
 * - Otherwise the path is reduced to its CDN-relative `ally-ai-testing-assets/…`
 *   segment and joined to the AI assets base for the given audit `cdnDirectory`.
 *   This tolerates reports that store an absolute/local path (e.g.
 *   `P:\…\ally-ai-testing-assets\image\…jpeg`) as well as already-relative paths.
 * Returns undefined when the path is missing so the caller can show a placeholder.
 */
export function aiAssetUrl(
  path: string | null | undefined,
  cdnDirectory: string | null | undefined,
): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  if (!cdnDirectory) return undefined;
  const normalized = path.replace(/\\/g, '/');
  const markerIndex = normalized.lastIndexOf(AI_ASSETS_DIR);
  const rel = (
    markerIndex >= 0 ? normalized.slice(markerIndex) : normalized
  ).replace(/^\/+/, '');
  const dir = cdnDirectory.replace(/^\/+|\/+$/g, '');
  return `${AI_ASSETS_CDN_BASE}${dir}/${rel}`;
}

/**
 * Resolves a crawler-report asset beside the page's regular screenshot URL.
 * These files live on the report CDN rather than under the AI asset directory.
 */
export function reportAssetUrl(
  path: string | null | undefined,
  pageScreenshotUrl: string | null | undefined,
): string | undefined {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  if (!pageScreenshotUrl) return undefined;

  const normalized = path.replace(/\\/g, '/').replace(/^\/+/, '');
  const marker = '/screenshots/';
  const markerIndex = pageScreenshotUrl.lastIndexOf(marker);
  if (markerIndex < 0) return undefined;

  const reportBase = `${pageScreenshotUrl.slice(0, markerIndex)}/`;
  try {
    return new URL(normalized, reportBase).toString();
  } catch {
    return undefined;
  }
}
